<!-- eslint-disable vue/multi-word-component-names -->
<!-- eslint-disable vue/valid-v-slot -->
<template>
  <v-container fluid>
    <v-row align="center" justify="space-between" class="m-4 mx-1 mt-5 mb-4">
      <h2 class="font-weight-bold">{{ $t("inventory") }}</h2>
    </v-row>

    <v-row align="center" class="mt-5 mb-4">
      <v-col cols="12" md="2">
        <v-select
          v-model="filterStatus"
          :items="translatedStatusOptions"
          :label="$t('filter_by_status')"
          item-text="name"
          item-value="id"
          dense
          outlined
          clearable
          prepend-inner-icon="mdi-filter"
          @change="resetPageAndFetch"
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-text-field
          v-model="barcode"
          :label="$t('search_by_barcode')"
          @input="handleInput"
          dense
          outlined
          :key="autoKey"
          ref="barcodeInput"
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-text-field
          :label="$t('filter_by_product_name')"
          outlined
          dense
          v-model="filter"
        />
      </v-col>
      
    </v-row>
    <v-card>
      <v-data-table
        :headers="headers"
        :items="inventory"
        :server-items-length="totalItems"
        :loading="loading"
        :options.sync="tableOptions"
        :footer-props="{
          'items-per-page-options': [10, 20, 50],
          'items-per-page-text': $t('rows_per_page'),
        }"
        class="elevation-1"
      >
        <template
          v-slot:footer.page-text="{ pageStart, pageStop, itemsLength }"
        >
          {{
            $t("pagination", {
              start: pageStart,
              end: pageStop,
              total: itemsLength,
            })
          }}
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip :color="statusColor(item.status)" text-color="white" small>
            {{ $t(item.status.replaceAll(" ", "_").toLowerCase()) }}
          </v-chip>
        </template>
        <template v-slot:item.expired_quantity="{ item }">
          <span :class="{ 'error--text font-weight-bold': parseFloat(item.expired_quantity) > 0 }">
            {{ item.expired_quantity }}
          </span>
        </template>
        <template v-slot:item.expiry_status="{ item }">
          <v-chip :color="expiryStatusColor(item.expiry_status)" text-color="white" small>
            {{ $t(item.expiry_status.replaceAll(" ", "_").toLowerCase()) }}
          </v-chip>
        </template>
        <template v-slot:item.purchase_price_per_item="{ item }">
            {{ item.purchase_price_per_item | iqd }}
        </template>
        <template v-slot:item.selling_price_per_item="{ item }">
            {{ item.selling_price_per_item | iqd }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on">
                <v-icon>mdi-dots-horizontal</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item
                @click="
                  selectedItem = item;
                  printPrice(item);
                "
              >
                <v-list-item-title>{{ $t("print_price") }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </v-card>
    <div id="print-area" class="print-wrapper" style="display: none">
      <div class="label-box">
        <div><strong>{{ selectedItem?.name }}:</strong></div>
        <br>
        <div v-if="selectedItem?.unit !== 'quantity'">
          {{ selectedItem?.selling_price_per_item | iqd }} IQD</div
        >
        <div v-else>
          {{ selectedItem?.selling_price_per_item | iqd}} IQD <span v-if="selectedItem?.selling_price_multiple && selectedItem?.selling_price_multiple_count">| {{ selectedItem.selling_price_multiple | iqd  }} IQD /{{ selectedItem.selling_price_multiple_count }} box</span>
        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
import api from "../services/axios";

export default {
  data() {
    return {
      barcode: null,
      selectedItem: {},
      loading: false,
      filter: "",
      inventory: [],
      totalItems: 0,
      tableOptions: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: [],
      },
      filterStatus: null,
      autoKey: 0,
    };
  },
  computed: {
    headers() {
      return [
        { text: this.$t("id"), value: "id", sortable: true },
        { text: this.$t("product_name"), value: "name", sortable: false },
        { text: this.$t("number_of_boxes"), value: "total_boxes", sortable: false },
        { text: this.$t("number_of_products"), value: "total_available_items", sortable: false },
        { text: this.$t("expired_quantity"), value: "expired_quantity", sortable: false },
        {
          text: this.$t("purchased_price_per_product"),
          value: "purchase_price_per_item",
          sortable: false,
        },
        {
          text: this.$t("selling_price_per_item"),
          value: "selling_price_per_item",
          sortable: false,
        },
        { text: this.$t("status"), value: "status", sortable: false },
        { text: this.$t("expiry_status"), value: "expiry_status", sortable: false },
        { text: "", value: "actions", sortable: false, align: "right" },
      ];
    },
    translatedStatusOptions() {
      return [
        { name: this.$t("in_stock"), id: "in_stock" },
        { name: this.$t("out_of_stock"), id: "out_of_stock" },
        { name: this.$t("low_stock"), id: "low_stock" },
      ];
    },
  },
  watch: {
    tableOptions: {
      deep: true,
      handler: "fetchInventory",
    },
    filter: "resetPageAndFetch",
  },
  mounted() {
    this.fetchInventory();
    this.debouncedSearch = this.debounce(this.searchAction, 500);
  },
  methods: {
    debounce(func, wait) {
      let timeout;
      return function (...args) {
        const context = this;
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(context, args), wait);
      };
    },

    handleInput() {
      this.debouncedSearch();
    },

    async searchAction() {
      try {
        const res = await api.get(`/product/barcode?barcode=${this.barcode}`);
        if (res.data.status) {
          this.filter = res.data.data.name;
          this.barcode = null;
          this.autoKey++;
        }
      } catch (err) {
        console.error("Error fetching product details:", err);
      }
    },
    
    printLabels() {
      this.$nextTick(() => {
        const content = document.getElementById("print-area").innerHTML;
        const printWindow = window.open("", "", "width=600,height=600");
        printWindow.document.write(`
          <html>
            <head>
              <style>
                body { font-family: arial; font-size: 12px; }
                .label-box {
                  border: 1px solid black;
                  margin-bottom: 5px;
                  padding: 5px;
                  width: 7.5cm;
                  height: 2.7cm;
                  box-sizing: border-box;
                  display: flex;
                  flex-direction: column;
                  font-size: 19px;
                  align-items: center;
                  text-align: center;
                  justify-content: center;
                }
              </style>
            </head>
            <body>${content}</body>
          </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      });
    },
    statusColor(status) {
      switch (status) {
        case "in stock":
          return "green";
        case "out of stock":
          return "red";
        case "low stock":
          return "orange";
        default:
          return "grey";
      }
    },
    expiryStatusColor(status) {
      switch (status) {
        case "expired":
          return "red";
        case "contains expired stock":
          return "orange";
        default:
          return "green";
      }
    },
    resetPageAndFetch() {
      if (this.tableOptions.page !== 1) {
        this.tableOptions.page = 1;
        return;
      }

      this.fetchInventory();
    },
    async fetchInventory() {
      this.loading = true;
      try {
        const { page, itemsPerPage, sortBy, sortDesc } = this.tableOptions;
        const activeSortBy = Array.isArray(sortBy) ? sortBy[0] : sortBy;
        const activeSortDesc = Array.isArray(sortDesc)
          ? sortDesc[0]
          : sortDesc;
        const res = await api.get("/inventory", {
          params: {
            page,
            perPage: itemsPerPage,
            status: this.filterStatus || "",
            filter: this.filter,
            sort_by: activeSortBy === "id" ? "id" : undefined,
            sort_desc: activeSortBy === "id" && activeSortDesc ? 1 : 0,
          }
        });
        this.inventory = res.data.data.data;
        this.totalItems = res.data.data.total_records;
      } catch (error) {
        console.error("Failed to load inventory:", error);
      } finally {
        this.loading = false;
      }
    },
    printPrice(item) {
      console.log("Print price for:", item.product_name);
      this.printLabels();
      // Add real implementation for printing if needed
    },
  },
};
</script>

<style scoped>
.filter-select {
  max-width: 250px;
}
h2 {
  font-size: 24px;
}
</style>
